import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { 
  FileText, 
  BookOpen, 
  Clock, 
  ChevronRight,
  Search,
  Loader2
} from 'lucide-react';
import { Input } from '../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DocumentationPage() {
  const { getAuthHeader } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docContent, setDocContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingContent, setLoadingContent] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await fetch(`${API}/api/docs/list`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocContent = async (filename) => {
    setLoadingContent(true);
    try {
      const response = await fetch(`${API}/api/docs/view/${filename}`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setDocContent(data);
        setSelectedDoc(filename);
      }
    } catch (error) {
      console.error('Error fetching document:', error);
    } finally {
      setLoadingContent(false);
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getDocCategory = (filename) => {
    const name = filename.toLowerCase();
    if (name.includes('prd') || name.includes('requirement')) return { label: 'Requirements', color: 'bg-blue-100 text-blue-700' };
    if (name.includes('logic') || name.includes('flow')) return { label: 'Business Logic', color: 'bg-purple-100 text-purple-700' };
    if (name.includes('guide') || name.includes('instruction')) return { label: 'Guide', color: 'bg-green-100 text-green-700' };
    if (name.includes('changelog') || name.includes('log')) return { label: 'Changelog', color: 'bg-orange-100 text-orange-700' };
    if (name.includes('proposal') || name.includes('design')) return { label: 'Proposal', color: 'bg-amber-100 text-amber-700' };
    if (name.includes('cleanup') || name.includes('refactor')) return { label: 'Technical', color: 'bg-slate-100 text-slate-700' };
    return { label: 'Document', color: 'bg-gray-100 text-gray-700' };
  };

  const filteredDocs = documents.filter(doc => 
    doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.filename.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Simple markdown to HTML converter
  const renderMarkdown = (content) => {
    if (!content) return null;
    
    // Process the content line by line
    const lines = content.split('\n');
    const elements = [];
    let inCodeBlock = false;
    let codeContent = [];
    let inTable = false;
    let tableRows = [];
    
    lines.forEach((line, idx) => {
      // Code blocks
      if (line.startsWith('```')) {
        if (inCodeBlock) {
          elements.push(
            <pre key={`code-${idx}`} className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-sm my-4 font-mono">
              <code>{codeContent.join('\n')}</code>
            </pre>
          );
          codeContent = [];
        }
        inCodeBlock = !inCodeBlock;
        return;
      }
      
      if (inCodeBlock) {
        codeContent.push(line);
        return;
      }

      // Tables
      if (line.startsWith('|') && line.endsWith('|')) {
        if (!inTable) {
          inTable = true;
          tableRows = [];
        }
        // Skip separator rows
        if (!line.match(/^\|[\s\-:|]+\|$/)) {
          tableRows.push(line);
        }
        return;
      } else if (inTable) {
        // End of table
        elements.push(
          <div key={`table-${idx}`} className="overflow-x-auto my-4">
            <table className="min-w-full border border-border rounded-lg">
              <tbody>
                {tableRows.map((row, rowIdx) => {
                  const cells = row.split('|').filter(c => c.trim());
                  const isHeader = rowIdx === 0;
                  return (
                    <tr key={rowIdx} className={isHeader ? 'bg-muted' : 'hover:bg-muted/50'}>
                      {cells.map((cell, cellIdx) => (
                        isHeader ? 
                          <th key={cellIdx} className="px-4 py-2 text-left font-semibold border-b text-sm">{cell.trim()}</th> :
                          <td key={cellIdx} className="px-4 py-2 border-b text-sm">{cell.trim()}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
        inTable = false;
        tableRows = [];
      }

      // Headers
      if (line.startsWith('# ')) {
        elements.push(<h1 key={idx} className="text-3xl font-bold mt-8 mb-4 text-primary">{line.slice(2)}</h1>);
        return;
      }
      if (line.startsWith('## ')) {
        elements.push(<h2 key={idx} className="text-2xl font-bold mt-6 mb-3 text-foreground border-b pb-2">{line.slice(3)}</h2>);
        return;
      }
      if (line.startsWith('### ')) {
        elements.push(<h3 key={idx} className="text-xl font-semibold mt-5 mb-2 text-foreground">{line.slice(4)}</h3>);
        return;
      }
      if (line.startsWith('#### ')) {
        elements.push(<h4 key={idx} className="text-lg font-semibold mt-4 mb-2 text-muted-foreground">{line.slice(5)}</h4>);
        return;
      }

      // Horizontal rule
      if (line.match(/^[-*_]{3,}$/)) {
        elements.push(<Separator key={idx} className="my-6" />);
        return;
      }

      // Bullet points
      if (line.match(/^[-*]\s/)) {
        elements.push(
          <div key={idx} className="flex gap-2 ml-4 my-1">
            <span className="text-primary">•</span>
            <span className="text-sm">{line.slice(2)}</span>
          </div>
        );
        return;
      }

      // Numbered list
      if (line.match(/^\d+\.\s/)) {
        const num = line.match(/^(\d+)\./)[1];
        elements.push(
          <div key={idx} className="flex gap-2 ml-4 my-1">
            <span className="text-primary font-medium">{num}.</span>
            <span className="text-sm">{line.replace(/^\d+\.\s/, '')}</span>
          </div>
        );
        return;
      }

      // Bold text
      let processedLine = line.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
      // Inline code
      processedLine = processedLine.replace(/`([^`]+)`/g, '<code class="bg-muted px-1.5 py-0.5 rounded text-sm font-mono">$1</code>');

      // Empty lines
      if (line.trim() === '') {
        elements.push(<div key={idx} className="h-2" />);
        return;
      }

      // Regular paragraphs
      elements.push(
        <p key={idx} className="text-sm leading-relaxed my-2" dangerouslySetInnerHTML={{ __html: processedLine }} />
      );
    });

    return elements;
  };

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-primary" />
            Documentation
          </h1>
          <p className="text-muted-foreground mt-1">
            System documentation, proposals, and technical specifications
          </p>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Document List */}
          <div className="col-span-12 lg:col-span-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Documents</CardTitle>
                <div className="relative mt-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search documents..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : (
                  <ScrollArea className="h-[calc(100vh-320px)]">
                    <div className="space-y-1 p-2">
                      {filteredDocs.map((doc) => {
                        const category = getDocCategory(doc.filename);
                        const isSelected = selectedDoc === doc.filename;
                        
                        return (
                          <button
                            key={doc.filename}
                            onClick={() => fetchDocContent(doc.filename)}
                            className={`w-full text-left p-3 rounded-lg transition-colors ${
                              isSelected 
                                ? 'bg-primary/10 border border-primary/30' 
                                : 'hover:bg-muted'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <FileText className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                              <div className="flex-1 min-w-0">
                                <p className={`font-medium text-sm truncate ${isSelected ? 'text-primary' : ''}`}>
                                  {doc.title}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge variant="secondary" className={`text-xs ${category.color}`}>
                                    {category.label}
                                  </Badge>
                                  <span className="text-xs text-muted-foreground">
                                    {formatFileSize(doc.size)}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                                  <Clock className="w-3 h-3" />
                                  {formatDate(doc.modified_at)}
                                </div>
                              </div>
                              <ChevronRight className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Document Content */}
          <div className="col-span-12 lg:col-span-8">
            <Card className="h-[calc(100vh-200px)]">
              {loadingContent ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : docContent ? (
                <>
                  <CardHeader className="border-b">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>{docContent.title}</CardTitle>
                        <CardDescription className="flex items-center gap-4 mt-1">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Last modified: {formatDate(docContent.modified_at)}
                          </span>
                          <span>{formatFileSize(docContent.size)}</span>
                        </CardDescription>
                      </div>
                      <Badge variant="secondary" className={getDocCategory(docContent.filename).color}>
                        {getDocCategory(docContent.filename).label}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <ScrollArea className="h-[calc(100vh-340px)]">
                      <div className="p-6">
                        {renderMarkdown(docContent.content)}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                  <BookOpen className="w-16 h-16 mb-4 opacity-30" />
                  <p className="text-lg font-medium">Select a document to view</p>
                  <p className="text-sm">Choose from the list on the left</p>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
